// SearchCircleLayer — zeichnet den Suchkreis der Kreissuche als Canvas-OverlayView im markerLayer, garantiert ÜBER
// den flachen Bild-Overlays.
//
// Warum überhaupt: `google.maps.Circle` liegt auf Googles Vektor-Fläche und lässt sich per API NICHT über ein
// Bild-Overlay heben — ein zIndex am Kreis bleibt wirkungslos. Die flachen Overlays (QuadOverlayLayer) hängen als
// DOM-Divs im `overlayLayer` (Pane 1) und deckten den Kreis deshalb zu. Dieselbe Sackgasse und dieselbe Lösung wie
// bei der Verortungs-Grafik, siehe marker/LocationShapeLayer.js — der Canvas-Aufbau hier ist von dort übernommen.
//
// MODELL/ANSICHT: das `google.maps.Circle` BLEIBT bestehen und ist weiter die Geometrie-Wahrheit (Konsumenten nutzen
// getCenter/getRadius/getBounds, teils an CircleSearch vorbei) — es wird nur unsichtbar geschaltet. Dieser Layer ist
// reine Projektion davon und hört auf `center_changed`/`radius_changed`, damit JEDER Mutationspfad ankommt.
//
// google ist Runtime-Global (nach Maps-API-Load); die geometry-Bibliothek wird ohnehin geladen (computeDistanceBetween
// im GoogleMapsAdapter).

// Umriss als Polygonzug aus N Punkten auf dem Kreis: der Radius steht in METERN, ein Pixel-Radius wäre in Mercator nur
// eine Näherung. So deckt sich die gezeichnete Fläche exakt mit dem Suchradius.
const SEGMENTS = 72;
// Optik wie der bisherige google.maps.Circle (dessen Defaults: strokeWeight 3, fillOpacity 0.35). Nachjustierbar.
const STROKE_COLOR = '#c356a0';
const STROKE_WIDTH = 3;
const FILL_COLOR = 'rgba(195, 86, 160, 0.35)';   // #c356a0 mit 35 % Deckkraft

export class SearchCircleLayer {
    #map;
    #circle;
    #overlay;
    #canvas = null;
    #ctx = null;
    #listeners = [];

    constructor(map, circle) {
        this.#map = map;
        this.#circle = circle;
        this.#overlay = new google.maps.OverlayView();
        this.#overlay.onAdd = () => this.#onAdd();
        this.#overlay.draw = () => this.#draw();
        this.#overlay.onRemove = () => this.#onRemove();
        this.#overlay.setMap(map);
        // Googles eigene Darstellung aus — die Geometrie bleibt, nur gezeichnet wird sie jetzt hier.
        if (this.#circle && this.#circle.setVisible) { this.#circle.setVisible(false); }
        // Jede Änderung am Kreis neu zeichnen, egal wer sie auslöst (CircleSearch, MapController.drawAreaCircle, …).
        if (this.#circle) {
            this.#listeners.push(
                google.maps.event.addListener(this.#circle, 'center_changed', () => this.redraw()),
                google.maps.event.addListener(this.#circle, 'radius_changed', () => this.redraw())
            );
        }
    }

    redraw() { if (this.#canvas) { this.#draw(); } }

    destroy() {
        for (const l of this.#listeners) { google.maps.event.removeListener(l); }
        this.#listeners = [];
        if (this.#overlay) { this.#overlay.setMap(null); }   // triggert onRemove → Canvas raus
        this.#overlay = null;
        this.#canvas = null;
        this.#ctx = null;
        this.#circle = null;
    }

    #onAdd() {
        const c = document.createElement('canvas');
        c.style.position = 'absolute';
        c.style.left = '0';
        c.style.top = '0';
        c.style.pointerEvents = 'none';   // reine Anzeige — Rechtsklick-Ziehen geht weiter an die Karte
        this.#canvas = c;
        this.#ctx = c.getContext('2d');
        const panes = this.#overlay.getPanes();
        // markerLayer (Pane 2): über dem overlayLayer (Pane 1) mit den flachen Overlays. Kein zIndex nötig — die
        // Marker liegen in derselben Pane, sollen aber ohnehin über dem Kreis bleiben (sie tragen einen).
        if (panes) { panes.markerLayer.appendChild(c); }
    }

    #onRemove() {
        if (this.#canvas && this.#canvas.parentNode) { this.#canvas.parentNode.removeChild(this.#canvas); }
        this.#canvas = null;
        this.#ctx = null;
    }

    #draw() {
        const proj = this.#overlay && this.#overlay.getProjection();
        if (!proj || !this.#canvas || !this.#ctx) { return; }

        // Canvas = Karten-Viewport: Ursprung (oben-links) in Div-Pixel projizieren, Canvas dorthin + Viewport-Größe.
        const mapDiv = this.#map.getDiv();
        const w = mapDiv.offsetWidth;
        const h = mapDiv.offsetHeight;
        const originLatLng = proj.fromContainerPixelToLatLng(new google.maps.Point(0, 0));
        const origin = originLatLng ? proj.fromLatLngToDivPixel(originLatLng) : null;
        if (!origin) { return; }
        this.#canvas.style.left = origin.x + 'px';
        this.#canvas.style.top = origin.y + 'px';
        // HiDPI: Backing-Store in Geräte-Pixeln, CSS-Box in logischen Pixeln, Zeichnen über eine dpr-skalierte Transform.
        const dpr = window.devicePixelRatio || 1;
        const bw = Math.round(w * dpr), bh = Math.round(h * dpr);
        if (this.#canvas.width !== bw) { this.#canvas.width = bw; }
        if (this.#canvas.height !== bh) { this.#canvas.height = bh; }
        this.#canvas.style.width = w + 'px';
        this.#canvas.style.height = h + 'px';

        const ctx = this.#ctx;
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        ctx.clearRect(0, 0, w, h);

        const center = this.#circle && this.#circle.getCenter ? this.#circle.getCenter() : null;
        const radius = this.#circle && this.#circle.getRadius ? this.#circle.getRadius() : 0;
        if (!center || !radius) { return; }   // kein Kreis gezogen → leerer Canvas

        const offset = google.maps.geometry.spherical.computeOffset;
        ctx.beginPath();
        for (let i = 0; i < SEGMENTS; i++) {
            const p = proj.fromLatLngToDivPixel(offset(center, radius, i * (360 / SEGMENTS)));
            const x = p.x - origin.x, y = p.y - origin.y;
            if (i === 0) { ctx.moveTo(x, y); } else { ctx.lineTo(x, y); }
        }
        ctx.closePath();
        ctx.fillStyle = FILL_COLOR;
        ctx.fill();
        ctx.strokeStyle = STROKE_COLOR;
        ctx.lineWidth = STROKE_WIDTH;
        ctx.stroke();
    }
}
